import { Input, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@creanizer/ui'
import type { WorldBuilderInput } from '@creanizer/types'

interface StandardModeFormProps {
  input: WorldBuilderInput
  onChange: (input: WorldBuilderInput) => void
}

const WORLD_SCALES = [
  { value: 'room', label: '部屋' },
  { value: 'building', label: '建物' },
  { value: 'city', label: '都市' },
  { value: 'region', label: '地域' },
  { value: 'country', label: '国家' },
  { value: 'continent', label: '大陸' },
  { value: 'planet', label: '惑星' },
  { value: 'solar_system', label: '恒星系' },
  { value: 'galaxy', label: '銀河' },
  { value: 'universe', label: '宇宙' },
  { value: 'multiverse', label: '多元宇宙' },
]

const GENRES = [
  { value: 'fantasy', label: 'ファンタジー' },
  { value: 'sci-fi', label: 'SF' },
  { value: 'cyberpunk', label: 'サイバーパンク' },
  { value: 'steampunk', label: 'スチームパンク' },
  { value: 'post-apocalyptic', label: 'ポストアポカリプス' },
  { value: 'urban-fantasy', label: 'アーバンファンタジー' },
  { value: 'horror', label: 'ホラー' },
  { value: 'mystery', label: 'ミステリー' },
  { value: 'historical', label: '歴史' },
  { value: 'mythology', label: '神話' },
  { value: 'modern', label: '現代風' },
  { value: 'war-chronicle', label: '戦記' },
  { value: 'legend', label: '伝奇' },
  { value: 'robot', label: 'ロボット' },
]

export function StandardModeForm({ input, onChange }: StandardModeFormProps) {
  const handleKeywordsChange = (value: string) => {
    onChange({
      ...input,
      keywords: value.split(',').map(k => k.trim()).filter(Boolean)
    })
  }
  
  // ジャンル選択はチェックボックスで直接処理
  
  return (
    <div className="space-y-4">
      <div>
        <Label htmlFor="worldName">世界観名</Label>
        <Input
          id="worldName"
          value={input.worldName || ''}
          onChange={(e) => onChange({ ...input, worldName: e.target.value })}
          placeholder="例: エルディア大陸"
        />
      </div>
      
      <div>
        <Label htmlFor="concept">コンセプト</Label>
        <Input
          id="concept"
          value={input.concept || ''}
          onChange={(e) => onChange({ ...input, concept: e.target.value })}
          placeholder="例: 魔法と科学が共存する世界"
        />
      </div>
      
      <div>
        <Label htmlFor="worldScale">世界の規模</Label>
        <Select
          value={input.worldScale || 'country'}
          onValueChange={(value) => onChange({ ...input, worldScale: value as any })}
        >
          <SelectTrigger id="worldScale">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {WORLD_SCALES.map((scale) => (
              <SelectItem key={scale.value} value={scale.value}>
                {scale.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>
      
      <div>
        <Label>ジャンル（複数選択可）</Label>
        <div className="grid grid-cols-3 gap-2 mt-2">
          {GENRES.map((genre) => (
            <label
              key={genre.value}
              className="flex items-center space-x-2 p-2 border rounded cursor-pointer hover:bg-accent"
            >
              <input
                type="checkbox"
                checked={input.genres?.includes(genre.value as any) || false}
                onChange={(e) => {
                  const genres = input.genres || []
                  const newGenres = e.target.checked
                    ? [...genres, genre.value as any]
                    : genres.filter(g => g !== genre.value)
                  onChange({ ...input, genres: newGenres })
                }}
              />
              <span className="text-sm">{genre.label}</span>
            </label>
          ))}
        </div>
      </div>
      
      <div>
        <Label htmlFor="keywords">キーワード（カンマ区切り）</Label>
        <Input
          id="keywords"
          value={input.keywords?.join(', ') || ''}
          onChange={(e) => handleKeywordsChange(e.target.value)}
          placeholder="例: 浮遊島, 古代遺跡, 魔導技術"
        />
      </div>
      
      <div>
        <Label htmlFor="customGenre">カスタムジャンル（オプション）</Label>
        <Input
          id="customGenre"
          value={input.customGenre || ''}
          onChange={(e) => onChange({ ...input, customGenre: e.target.value })}
          placeholder="例: ニューウィアード"
        />
      </div>
    </div>
  )
}